import { Button, Modal, Spin, Tabs, Typography, message, Input } from 'antd';
import React, { useCallback, useEffect, useState } from 'react';
import { getApplicantByEmailApiRequest } from '../../../api/applicant';
import {
  AcademicInfo,
  ContactInfo,
  PersonalInfo,
  StatusInfo,
} from './components';
import RequirementsSection from './components/RequirementsSection';

const { Title, Text } = Typography;
const { TextArea } = Input;

/**
 * Modal component for reviewing applicant details with focus on those with incomplete status
 * @param {Object} props - Component props
 * @param {boolean} props.visible - Whether the modal is visible
 * @param {Function} props.onClose - Function to call when modal is closed
 * @param {Object} props.applicant - Initial applicant data
 * @param {Function} props.onApprove - Function to call when applicant is approved
 * @param {Function} props.onReject - Function to call when applicant is rejected
 */
const ApplicantReviewModal = ({
  visible,
  onClose,
  applicant,
  onApprove = () => {},
  onReject = () => {},
}) => {
  // Component state
  const [loading, setLoading] = useState(false);
  const [applicantDetails, setApplicantDetails] = useState(null);
  const [approvalLoading, setApprovalLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('1');
  const [rejectReason, setRejectReason] = useState('');
  const [showRejectInput, setShowRejectInput] = useState(false);

  // Fetch applicant details function
  const fetchApplicantDetails = useCallback(async (email) => {
    if (!email) return;

    setLoading(true);
    try {
      const response = await getApplicantByEmailApiRequest(email);

      if (response.success) {
        setApplicantDetails(response.data);
      } else {
        message.error('Failed to fetch applicant details');
        handleClose();
      }
    } catch (error) {
      console.error('Error fetching applicant details:', error);
      message.error('Failed to fetch applicant details');
      handleClose();
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch detailed applicant information when modal opens
  useEffect(() => {
    if (visible && applicant?.email) {
      fetchApplicantDetails(applicant.email);
    }
  }, [visible, applicant, fetchApplicantDetails]);

  // Reset state when modal closes
  useEffect(() => {
    if (!visible) {
      setActiveTab('1');
      setRejectReason('');
      setShowRejectInput(false);
    }
  }, [visible]);

  /**
   * Handle modal close
   */
  const handleClose = () => {
    // Reset all state values to initial state
    setApplicantDetails(null);
    setRejectReason('');
    setShowRejectInput(false);
    onClose();
  };

  /**
   * Handle rejection of requirements
   */
  const handleRejectRequirements = async () => {
    if (!rejectReason || !rejectReason.trim()) {
      message.error('Please provide a reason for rejection');
      return;
    }

    setApprovalLoading(true);
    try {
      // Call the parent component's onReject function
      await onReject(applicantDetails?._id || applicant?._id, rejectReason);
      message.success('Applicant requirements have been rejected');
      handleClose();
    } catch (error) {
      console.error('Error rejecting requirements:', error);
      message.error('Failed to reject requirements: ' + (error.message || 'Unknown error'));
    } finally {
      setApprovalLoading(false);
    }
  };

  // Define tab items for the Tabs API with memoization to prevent unnecessary re-renders
  const tabItems = React.useMemo(
    () => [
      {
        key: '1',
        label: 'Personal Info',
        children: applicant && applicantDetails && <PersonalInfo data={applicantDetails} />,
      },
      {
        key: '2',
        label: 'Academic Info',
        children: applicant && applicantDetails && <AcademicInfo data={applicantDetails} />,
      },
      {
        key: '3',
        label: 'Contact Info',
        children: applicant && applicantDetails && <ContactInfo data={applicantDetails} />,
      },
      {
        key: '4',
        label: 'Status',
        children: applicant && applicantDetails && <StatusInfo data={applicantDetails} />,
      },
      {
        key: '5',
        label: 'Requirements',
        children: applicant && applicantDetails && (
          <RequirementsSection
            admissionRequirements={applicantDetails?.admissionRequirements}
            enrollmentRequirements={applicantDetails?.enrollmentRequirements || []}
            voucherRequirements={applicantDetails?.voucherRequirements || []} // Added prop
            applicantId={applicantDetails?._id}
            enrollmentRequirementsStatus={applicantDetails?.enrollmentRequirementsStatus || "Incomplete"}
            admissionRequirementsStatus={applicantDetails?.admissionRequirementsStatus || "Incomplete"}
            voucherApplicationStatus={applicantDetails?.voucherApplicationStatus || "Incomplete"} // Added prop
          />
        ),
      },
    ],
    [applicant, applicantDetails]
  );

  // Modal footer buttons with conditional rendering based on showRejectInput state
  const renderFooterButtons = () => {
    if (showRejectInput) {
      return [
        <Button key="back" onClick={() => setShowRejectInput(false)}>
          Cancel
        </Button>,
        <Button
          key="reject"
          type="primary"
          danger
          loading={approvalLoading}
          onClick={handleRejectRequirements}
        >
          Confirm Rejection
        </Button>,
      ];
    }

    return [];
  };

  const renderFooterClose = () => {
    return [
      <Button key="back" onClick={handleClose}>
        Close
      </Button>,
    ];
  };

  return (
    <Modal
      title={
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <Title level={4} style={{ margin: 0 }}>
            Review Applicant with Incomplete Requirements
          </Title>
          <Text type="secondary">
            {applicant ? `${applicant.firstName} ${applicant.lastName} (${applicant.email})` : ''}
          </Text>
        </div>
      }
      open={visible}
      onCancel={handleClose}
      width={900}
      centered
      destroyOnClose
      style={{ top: 20 }}
      styles={{
        body: {
          maxHeight: 'calc(100vh - 200px)',
          overflowY: 'auto',
          padding: '20px',
        },
      }}
      footer={
        applicant
          ? applicant?.enrollmentRequirementsStatus === "Complete" &&
            applicant?.admissionRequirementsStatus === "Complete"
            ? renderFooterButtons()
            : renderFooterClose()
          : renderFooterClose()
      }
    >
      <Spin spinning={loading} tip="Loading applicant details...">
        {showRejectInput ? (
          <div style={{ marginBottom: '20px' }}>
            <Title level={5}>Provide Reason for Rejection</Title>
            <TextArea
              rows={4}
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Please explain why the requirements are being rejected..."
            />
          </div>
        ) : (
          applicant &&
          applicantDetails && (
            <Tabs
              defaultActiveKey="1"
              activeKey={activeTab}
              onChange={setActiveTab}
              type="card"
              size="large"
              style={{ marginBottom: '16px' }}
              destroyInactiveTabPane
              items={tabItems}
            />
          )
        )}
      </Spin>
    </Modal>
  );
};

export default ApplicantReviewModal;